import { ErrorAlert } from "@components/ErrorAlert";
import { queriesRequirementDefinitions } from "@query";
import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSText
} from "@wingspanhq/fe-component-library";
import { RequirementDefinition } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { DefaultActionDetails } from "../components/DefaultActionDetails";

export interface ArchiveRequirementModalProps {
  requirementDefinition: RequirementDefinition;
  engagementLength: number;
  onClose: () => void;
}

export const ArchiveRequirementModal: React.FC<
  ArchiveRequirementModalProps
> = ({ requirementDefinition, engagementLength, onClose }) => {
  const { openSnackbar } = useWSSnackbar();
  const [deactivateRequirementDefinition, deactivateRequirementDefinitionMeta] =
    queriesRequirementDefinitions.deactivateRequirementDefinition.useMutation();

  const handleArchive = async () => {
    await deactivateRequirementDefinition(
      {
        requirementDefinitionId: requirementDefinition.id!
      },
      {
        onSuccess: () => {
          openSnackbar({
            theme: "dark",
            type: "success",
            message: "Requirement archived"
          });
          onClose();
        }
      }
    );
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to archive this requirement?
      </WSText.ParagraphSm>
      <DefaultActionDetails
        engagementLength={engagementLength}
        requirementDefinition={requirementDefinition}
      />

      <WSAlert
        icon="info-circle"
        my="2XL"
        size="M"
        theme="info"
        title="Archiving requirement - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>
            Historical data (e.g. requirement completion) will be retained if
            the requirement is restored and added to engagement types.
          </li>
        </WSElement>
      </WSAlert>

      <ErrorAlert
        context="UpdateRequirement"
        error={deactivateRequirementDefinitionMeta.error}
        mb="XL"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Archive requirement",
            loading: deactivateRequirementDefinitionMeta.isLoading,
            onAsyncClick: handleArchive,
            kind: "Primary"
          }
        ]}
      />
    </>
  );
};

export const useArchiveRequirementModal = () => {
  return useWSModal(ArchiveRequirementModal, {
    title: "Archive requirement",
    size: "M"
  });
};
